import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useActiveAquarium } from '../contexts/AquariumContext';

/**
 * Matches an aquarium-scoped path segment, e.g. /aquariums/42/gallery.
 * Captures the raw id segment as a string regardless of whether the
 * underlying activeAquariumId is numeric or a UUID string.
 */
const AQUARIUM_SCOPED_PATH_REGEX = /\/aquariums\/([^/]+)/;

/**
 * useAquariumRouteSync
 *
 * Watches activeAquariumId from ActiveAquariumContext (the header switcher's
 * context — see contexts/AquariumContext.tsx) and, whenever it CHANGES
 * (never on initial mount — even if the value was restored from
 * localStorage), swaps the :aquariumId segment of the current URL for the
 * new id using navigate(newPath, { replace: true }).
 *
 * On non-aquarium-scoped routes (e.g. /settings) this hook is a no-op:
 * it updates no URL and does not navigate.
 *
 * The other direction too: when the URL names one of the user's aquariums
 * (a link, the aquarium list, browser back, a bookmark), that aquarium
 * becomes the active one. Otherwise the switcher could show tank B while the
 * page — and every measurement saved on it — belongs to tank A.
 *
 * Must be mounted inside the Router context — see AquariumRouteSync, mounted
 * once at the App.tsx root alongside <Routes>.
 */
export function useAquariumRouteSync(): void {
  const { aquariums, activeAquariumId, setActiveAquarium } = useActiveAquarium();
  const navigate = useNavigate();
  const location = useLocation();

  // Tracks the previous activeAquariumId so we can distinguish the first
  // render (mount) from a genuine change while already mounted.
  const previousAquariumIdRef = useRef<string | null>(activeAquariumId);
  const isFirstRenderRef = useRef<boolean>(true);

  // location is read via a ref rather than being included in the effect's
  // dependency array. The effect must only re-run when selectedAquariumId
  // changes (a route change unrelated to the active aquarium must not
  // trigger a navigation). Reading location.pathname through a ref keeps
  // the effect closure up to date with the latest path without causing
  // it to re-fire on every route change.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // URL → active aquarium. Applied once per URL id (until the context has
  // taken it over), so a switcher choice is never overridden by the URL it
  // is about to replace.
  const routeAquariumId = AQUARIUM_SCOPED_PATH_REGEX.exec(location.pathname)?.[1] ?? null;
  const appliedRouteIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (routeAquariumId === null || routeAquariumId === appliedRouteIdRef.current) return;
    // Only real aquariums — not /aquariums/new, nor a list still loading.
    if (!aquariums.some((a) => a.id === routeAquariumId)) return;
    if (activeAquariumId === routeAquariumId) {
      appliedRouteIdRef.current = routeAquariumId;
      return;
    }
    setActiveAquarium(routeAquariumId);
  }, [routeAquariumId, aquariums, activeAquariumId, setActiveAquarium]);

  useEffect(() => {
    // Never navigate on the very first render, even if activeAquariumId
    // is already populated (e.g. restored from localStorage on mount).
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      previousAquariumIdRef.current = activeAquariumId;
      return;
    }

    const previousAquariumId = previousAquariumIdRef.current;
    previousAquariumIdRef.current = activeAquariumId;

    // No-op if the id did not actually change.
    if (previousAquariumId === activeAquariumId) {
      return;
    }

    // No-op if there is no active aquarium to swap to.
    if (activeAquariumId === null) {
      return;
    }

    // null → id is the initial resolution once the aquarium list has loaded
    // (fallback to the first aquarium), not a switch by the user: keep the URL.
    if (previousAquariumId === null) {
      return;
    }

    const currentPath = locationRef.current.pathname;
    const match = AQUARIUM_SCOPED_PATH_REGEX.exec(currentPath);

    // No-op on non-aquarium-scoped routes (e.g. /settings).
    if (!match) {
      return;
    }

    const currentSegmentId = match[1];

    // Avoid a redundant navigation if the path already reflects the new id.
    if (currentSegmentId === activeAquariumId) {
      return;
    }

    const newPath = currentPath.replace(
      AQUARIUM_SCOPED_PATH_REGEX,
      `/aquariums/${activeAquariumId}`,
    );

    navigate(newPath, { replace: true });
  }, [activeAquariumId, navigate]);
}
